import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { INDUSTRIES, DEFAULT_HOURS } from '../../utils/industrySchemas'
import LoadingSpinner from '../common/LoadingSpinner'
import {
  Building2, UtensilsCrossed, Clock, GalleryHorizontal, MapPin, Search, ShoppingBag,
  Wrench, Users, Dumbbell, CalendarDays, Package, LayoutGrid, Star,
  ShieldCheck, FileText, ChevronRight, ChevronLeft, Plus, X, Trash2,
  Image as ImageIcon, Upload, Rocket, Check, Loader2,
} from 'lucide-react'
import '../../styles/editor-mobile.css'

// ─── helpers ─────────────────────────────────────────────────────────────────

async function uploadImage(file, clientId) {
  const form = new FormData()
  form.append('files', file)
  if (clientId) form.append('clientId', clientId)
  const res = await apiService.post('/media/upload', form)
  return res.data.files?.[0]?.public_url || ''
}

const TAB_ICONS = {
  business: Building2, menu: UtensilsCrossed, hours: Clock, gallery: GalleryHorizontal,
  contact: MapPin, seo: Search, products: ShoppingBag, services: Wrench,
  team: Users, trainers: Users, classes: Dumbbell, schedule: CalendarDays,
  membership: Package, packages: Package, portfolio: LayoutGrid,
  testimonials: Star, insurance: ShieldCheck,
}
const TabIcon = ({ id }) => { const I = TAB_ICONS[id] || FileText; return <I size={18} /> }

function tabSubtitle(tab, content) {
  const f0 = tab.fields[0]
  if (!f0) return ''
  if (f0.type === 'hours') return 'Opening hours'
  if (f0.type === 'multi-image') {
    const n = (content[f0.key] || []).length
    return `${n} image${n === 1 ? '' : 's'}`
  }
  if (f0.type === 'list') {
    const items = content[f0.key] || []
    if (f0.variant === 'menu') {
      const cats = new Set(items.map(it => (it.category || '').trim()).filter(Boolean))
      return `${cats.size} categor${cats.size === 1 ? 'y' : 'ies'} · ${items.length} item${items.length === 1 ? '' : 's'}`
    }
    return `${items.length} ${items.length === 1 ? 'item' : 'items'}`
  }
  for (const f of tab.fields) {
    const v = content[f.key]
    if (typeof v === 'string' && v.trim()) return v
  }
  return tab.fields.map(f => f.label).slice(0, 3).join(' · ')
}

// ─── small building blocks ───────────────────────────────────────────────────

const MField = ({ label, hint, children }) => (
  <div className="m-field">
    {label && <label>{label}</label>}
    {children}
    {hint && <div className="m-hint">{hint}</div>}
  </div>
)

const MSwitch = ({ on, onChange }) => (
  <button type="button" className={`m-switch ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => onChange(!on)}>
    <span className="thumb" />
  </button>
)

const MImgSlot = ({ value, onChange, clientId, size = 56, height, style }) => {
  const [up, setUp] = useState(false)
  const onFile = async (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    setUp(true)
    try { onChange(await uploadImage(f, clientId)) }
    catch { toast.error('Upload failed') }
    finally { setUp(false) }
  }
  return (
    <label className="m-imgslot" style={{ width: height ? undefined : size, height: height || size, ...style }}>
      {up ? <Loader2 className="animate-spin" size={16} /> : value ? <img src={value} alt="" /> : <ImageIcon size={height ? 22 : 16} />}
      <span className="m-imgslot-badge"><Upload size={13} /></span>
      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} disabled={up} />
    </label>
  )
}

const MMultiImageField = ({ field, value = [], onChange, clientId }) => {
  const [up, setUp] = useState(false)
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUp(true)
    try { onChange([...value, ...(await Promise.all(files.map(f => uploadImage(f, clientId))))]) }
    catch { toast.error('Upload failed') }
    finally { setUp(false) }
  }
  return (
    <MField label={field.label}>
      <div className="m-gallery-grid">
        {value.map((url, i) => (
          <div key={i} className="m-imgslot" style={{ aspectRatio: 1 }}>
            <img src={url} alt="" />
            <button type="button" className="m-gallery-del" onClick={() => onChange(value.filter((_, x) => x !== i))}><X size={12} /></button>
          </div>
        ))}
        <label className="m-imgslot" style={{ aspectRatio: 1, border: '1.5px dashed var(--border-strong)' }}>
          {up ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} style={{ color: 'var(--text-faint)' }} />}
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onFiles} disabled={up} />
        </label>
      </div>
    </MField>
  )
}

const MHoursField = ({ value, onChange }) => {
  const rows = Array.isArray(value) && value.length ? value : DEFAULT_HOURS
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((r, i) => (
        <div className="m-day-card" key={r.day}>
          <div className="day-head">
            <span className="name"><Clock size={13} style={{ color: 'var(--text-faint)' }} />{r.day}</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              Closed
              <MSwitch on={!r.open} onChange={(v) => update(i, { open: !v })} />
            </label>
          </div>
          <div className={`day-times ${!r.open ? 'disabled' : ''}`}>
            <input type="time" className="input" value={r.openTime || ''} onChange={(e) => update(i, { openTime: e.target.value })} />
            <input type="time" className="input" value={r.closeTime || ''} onChange={(e) => update(i, { closeTime: e.target.value })} />
          </div>
        </div>
      ))}
    </div>
  )
}

const MMenuField = ({ value, onChange, clientId }) => {
  const items = Array.isArray(value) ? value : []
  const [extraCats, setExtraCats] = useState([])
  const [newCat, setNewCat] = useState('')
  const [filter, setFilter] = useState('all')

  const catOf = (it) => (it.category || '').trim()
  const derived = []
  items.forEach(it => { const c = catOf(it); if (c && !derived.includes(c)) derived.push(c) })
  const categories = [...derived, ...extraCats.filter(c => !derived.includes(c))]

  const addCategory = () => {
    const name = newCat.trim()
    if (!name || categories.includes(name)) return
    setExtraCats([...extraCats, name]); setNewCat(''); setFilter(name)
  }
  const removeCategory = (name) => {
    const count = items.filter(it => catOf(it) === name).length
    if (count > 0 && !window.confirm(`Remove "${name}" and its ${count} item${count > 1 ? 's' : ''}?`)) return
    onChange(items.filter(it => catOf(it) !== name))
    setExtraCats(extraCats.filter(c => c !== name))
    if (filter === name) setFilter('all')
  }
  const addItem = () => {
    const cat = filter !== 'all' ? filter : (categories[0] || '')
    onChange([...items, { category: cat, name: 'New item', description: '', price: '', image: '' }])
  }
  const updateItem = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx))

  const visibleCount = items.filter(it => filter === 'all' || catOf(it) === filter).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <div className="m-section-title" style={{ marginBottom: 10 }}>Categories</div>
        <div className="m-chip-row">
          <span className={`m-chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            All <span className="count">{items.length}</span>
          </span>
          {categories.map(cat => (
            <span key={cat} className={`m-chip ${filter === cat ? 'active' : ''}`} onClick={() => setFilter(cat)}>
              {cat}
              <span className="count">{items.filter(it => catOf(it) === cat).length}</span>
              <button type="button" className="rm" onClick={(e) => { e.stopPropagation(); removeCategory(cat) }}><X size={10} /></button>
            </span>
          ))}
        </div>
        <div className="m-chip-add" style={{ marginTop: 10 }}>
          <input className="input" placeholder="New category" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCategory()} />
          <button type="button" className="btn btn-sm btn-primary" onClick={addCategory}><Plus size={13} /> Add</button>
        </div>
      </div>

      <div>
        <div className="m-section-title" style={{ marginBottom: 10 }}>Items</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibleCount === 0 && <div className="m-empty">No items in this category yet.</div>}
          {items.map((it, idx) => {
            if (filter !== 'all' && catOf(it) !== filter) return null
            return (
              <div className="m-item-card" key={idx}>
                <div className="m-item-top">
                  <MImgSlot value={it.image} onChange={(v) => updateItem(idx, { image: v })} clientId={clientId} size={56} />
                  <div className="m-item-fields">
                    <input className="input" value={it.name || ''} onChange={(e) => updateItem(idx, { name: e.target.value })} placeholder="Item name" />
                    <div className="row2">
                      <input className="input" value={it.price || ''} onChange={(e) => updateItem(idx, { price: e.target.value })} placeholder="Price" style={{ fontFamily: 'var(--font-mono)' }} />
                      <select className="input" value={catOf(it)} onChange={(e) => updateItem(idx, { category: e.target.value })}>
                        {catOf(it) === '' && <option value="">— none —</option>}
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <button type="button" className="m-item-del" onClick={() => removeItem(idx)}><Trash2 size={15} /></button>
                </div>
                <textarea className="m-item-desc input" value={it.description || ''} onChange={(e) => updateItem(idx, { description: e.target.value })} placeholder="Description (optional)" rows={2} />
              </div>
            )
          })}
        </div>
        <button type="button" className="m-add-btn" style={{ marginTop: 12 }} onClick={addItem}><Plus size={15} /> Add menu item</button>
      </div>
    </div>
  )
}

const MListField = ({ field, value, onChange, clientId }) => {
  const items = Array.isArray(value) ? value : []
  const subs = field.subFields || []
  const addItem = () => {
    const it = {}
    subs.forEach(sf => { it[sf.key] = sf.type === 'multi-image' ? [] : '' })
    onChange([...items, it])
  }
  const updateItem = (idx, key, val) => onChange(items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)))
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.length === 0 && <div className="m-empty">Nothing here yet.</div>}
      {items.map((it, idx) => (
        <div className="m-item-card" key={idx}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="m-section-title">{it[subs[0]?.key] || `Item ${idx + 1}`}</span>
            <button type="button" className="m-item-del" onClick={() => removeItem(idx)}><Trash2 size={15} /></button>
          </div>
          {subs.map(sf => (
            <div key={sf.key}>{renderField(sf, it[sf.key], (v) => updateItem(idx, sf.key, v), clientId)}</div>
          ))}
        </div>
      ))}
      <button type="button" className="m-add-btn" onClick={addItem}><Plus size={15} /> {field.addLabel || 'Add item'}</button>
    </div>
  )
}

// Single field → mobile control. Also used for list sub-fields.
function renderField(field, value, onChange, clientId) {
  switch (field.type) {
    case 'text':
    case 'url':
      return (
        <MField label={field.label}>
          <input
            className="input"
            type={field.type === 'url' ? 'url' : 'text'}
            inputMode={field.type === 'url' ? 'url' : undefined}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
        </MField>
      )
    case 'textarea':
      return (
        <MField label={field.label}>
          <textarea className="input" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={4} />
        </MField>
      )
    case 'image':
      return (
        <MField label={field.label}>
          <MImgSlot value={value} onChange={onChange} clientId={clientId} height={140} style={{ width: '100%' }} />
        </MField>
      )
    case 'multi-image':
      return <MMultiImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'hours':
      return <MHoursField value={value} onChange={onChange} />
    case 'list':
      return field.variant === 'menu'
        ? <MMenuField value={value} onChange={onChange} clientId={clientId} />
        : <MListField field={field} value={value} onChange={onChange} clientId={clientId} />
    default:
      return null
  }
}

// ─── main ────────────────────────────────────────────────────────────────────

const MobileContentEditor = ({ siteId, clientId, onBack }) => {
  const queryClient = useQueryClient()
  const [screen, setScreen] = useState('list')  // 'list' | tab.id
  const [content, setContent] = useState({})
  const [justSaved, setJustSaved] = useState(false)

  const contentRef = useRef({})
  const loadedRef = useRef(false)
  const saveTimer = useRef(null)
  const flashTimer = useRef(null)

  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId },
  )

  const profileUrl = clientId ? `/clients/profile?clientId=${clientId}` : '/clients/profile'
  const { data: clientProfile, isLoading: profileLoading } = useQuery(
    ['client-profile', clientId || 'self'],
    () => apiService.get(profileUrl).then(r => r.data),
  )

  // Load field values once from meta_data.variables (the canonical location the
  // published site + desktop editor use). Loading once avoids autosave refetches
  // clobbering in-progress edits.
  useEffect(() => {
    if (!site?.meta_data || loadedRef.current) return
    try {
      const parsed = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : site.meta_data
      const vars = parsed?.variables || {}
      setContent(vars); contentRef.current = vars
    } catch { setContent({}); contentRef.current = {} }
    loadedRef.current = true
  }, [site])

  useEffect(() => () => { clearTimeout(saveTimer.current); clearTimeout(flashTimer.current) }, [])

  const saveMutation = useMutation(
    (vars) => {
      let meta = {}
      try { meta = typeof site?.meta_data === 'string' ? JSON.parse(site.meta_data) : (site?.meta_data || {}) } catch { meta = {} }
      return apiService.put(`/sites/${siteId}`, { meta_data: JSON.stringify({ ...meta, variables: vars }) })
    },
    {
      onSuccess: () => {
        setJustSaved(true)
        clearTimeout(flashTimer.current)
        flashTimer.current = setTimeout(() => setJustSaved(false), 1400)
      },
      onError: () => toast.error('Failed to save'),
    },
  )

  const publishMutation = useMutation(
    () => apiService.post(`/sites/${siteId}/publish`),
    { onSuccess: () => toast.success('Site published!'), onError: () => toast.error('Failed to publish') },
  )

  const updateField = useCallback((key, value) => {
    setContent(prev => {
      const next = { ...prev, [key]: value }
      contentRef.current = next
      clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => saveMutation.mutate(contentRef.current), 800)
      return next
    })
  }, [saveMutation])

  const handlePublish = async () => {
    clearTimeout(saveTimer.current)
    try {
      await saveMutation.mutateAsync(contentRef.current)
      await publishMutation.mutateAsync()
    } catch { /* toasts handled in mutations */ }
  }

  if (siteLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '60dvh' }}>
        <LoadingSpinner size="lg" text="Loading editor..." />
      </div>
    )
  }

  const industry = site?.industry || clientProfile?.industry || 'general'
  const schema = INDUSTRIES[industry] || INDUSTRIES.general
  const activeTab = schema.tabs.find(t => t.id === screen)
  const uploadClientId = clientId || clientProfile?.id
  const domain = site?.subdomain || site?.domain || ''

  return (
    <div className="m-app">
      <div className="m-topbar">
        <button className="m-icon-btn" onClick={() => (screen === 'list' ? onBack?.() : setScreen('list'))} aria-label="Back">
          <ChevronLeft size={20} />
        </button>
        <div className="m-topbar-title">
          {screen === 'list' ? (
            <>
              <span className="t1">{site?.name || 'Your site'}</span>
              {domain && <span className="t2">{domain}</span>}
            </>
          ) : (
            <span className="t1">{activeTab?.label}</span>
          )}
        </div>
        {screen === 'list' ? (
          <button className="btn btn-sm btn-primary" style={{ marginRight: 4 }} onClick={handlePublish} disabled={publishMutation.isLoading || saveMutation.isLoading}>
            {publishMutation.isLoading ? <Loader2 className="animate-spin" size={13} /> : <Rocket size={13} />} Publish
          </button>
        ) : (
          <span className="m-saved-pill" style={{ opacity: justSaved ? 1 : 0.55, transition: 'opacity .2s ease' }}>
            {saveMutation.isLoading ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} />}
            {saveMutation.isLoading ? 'Saving' : 'Saved'}
          </span>
        )}
      </div>

      {screen === 'list' ? (
        <div className="m-list">
          {schema.tabs.map(tab => (
            <button key={tab.id} className="m-section-row" onClick={() => setScreen(tab.id)}>
              <span className="m-section-icon"><TabIcon id={tab.id} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <div className="label">{tab.label}</div>
                <div className="sub">{tabSubtitle(tab, content)}</div>
              </span>
              <span className="chev"><ChevronRight size={16} /></span>
            </button>
          ))}
        </div>
      ) : (
        <div className="m-detail" key={screen}>
          {activeTab?.fields.map(f => (
            <div key={f.key}>{renderField(f, content[f.key], (v) => updateField(f.key, v), uploadClientId)}</div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MobileContentEditor
